/**
 * goscore.dev - Static Single Page Application Engine
 * Powered by MiniSearch client-side full-text search engine
 * Complies with strict XSS prevention (No innerHTML usage).
 */

import 'nano-analytics';
import MiniSearch from 'minisearch';

// Initialize Google Analytics with nano-analytics
const GA_MEASUREMENT_ID = 'G-KZ0JE5RGL5';
if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
  window.gtag('config', GA_MEASUREMENT_ID);
}

(function () {
  'use strict';

  // Hardcoded Featured Reports for Prototyping Fallback
  const FEATURED_MODULES = [
    { name: 'github.com/stretchr/testify', defaultGrade: 'C' },
    { name: 'github.com/gin-gonic/gin', defaultGrade: 'C' },
    { name: 'github.com/pkg/errors', defaultGrade: 'C' },
    { name: 'github.com/google/go-cmp', defaultGrade: 'C' },
    { name: 'github.com/sirupsen/logrus', defaultGrade: 'C' },
    { name: 'google.golang.org/protobuf', defaultGrade: 'C' },
    { name: 'gopkg.in/yaml.v2', defaultGrade: 'C' },
    { name: 'github.com/davecgh/go-spew', defaultGrade: 'C' },
    { name: 'google.golang.org/grpc', defaultGrade: 'C' },
    { name: 'github.com/pmezard/go-difflib', defaultGrade: 'C' },
    { name: 'github.com/golang/protobuf', defaultGrade: 'C' }
  ];

  const DEFAULT_MODULE = 'github.com/stretchr/testify';

  // MiniSearch Index Instance
  let miniSearchInstance = null;
  let allPackages = [...FEATURED_MODULES.map(m => m.name)];

  // DOM Cache
  const DOM = {
    searchContainer: document.getElementById('search-container'),
    searchForm: document.getElementById('search-form'),
    searchInput: document.getElementById('module-search-input'),
    autocompleteDropdown: document.getElementById('autocomplete-dropdown'),
    autocompleteItems: document.getElementById('autocomplete-items'),
    sidebarFilterInput: document.getElementById('sidebar-filter-input'),
    moduleListContainer: document.getElementById('module-list-container'),
    moduleCountBadge: document.getElementById('module-count-badge'),
    
    // Navigation Links
    navReportsLink: document.getElementById('nav-reports-link'),
    navAboutLink: document.getElementById('nav-about-link'),
    navContactLink: document.getElementById('nav-contact-link'),

    // States
    loadingState: document.getElementById('loading-state'),
    loadingModuleName: document.getElementById('loading-module-name'),
    errorState: document.getElementById('error-state'),
    errorMessageText: document.getElementById('error-message-text'),
    errorRetryBtn: document.getElementById('error-retry-btn'),
    reportView: document.getElementById('report-view'),
    aboutView: document.getElementById('about-view'),
    contactView: document.getElementById('contact-view'),

    // Report Overview
    reportModuleTitle: document.getElementById('report-module-title'),
    reportVersionTag: document.getElementById('report-version-tag'),
    reportTimestampText: document.getElementById('report-timestamp-text'),
    copyUrlBtn: document.getElementById('copy-url-btn'),
    copyBtnText: document.getElementById('copy-btn-text'),
    gradeBadge: document.getElementById('grade-badge'),
    numericalScore: document.getElementById('numerical-score'),
    gaugeBarFill: document.getElementById('gauge-bar-fill'),
    checksGridContainer: document.getElementById('checks-grid-container')
  };

  /**
   * Helper options for MiniSearch
   */
  const miniSearchOptions = {
    fields: ['path', 'name', 'org'],
    storeFields: ['path', 'name', 'org'],
    searchOptions: {
      boost: { name: 2, path: 1 },
      fuzzy: 0.2,
      prefix: true
    }
  };

  /**
   * Initialize & Load MiniSearch compiled index
   */
  async function initMiniSearch() {
    try {
      // 1. Try loading pre-compiled index for Cloudflare / GCS static hosting
      const res = await fetch('search-index.json');
      if (res.ok) {
        const jsonIndex = await res.json();
        miniSearchInstance = MiniSearch.loadJSON(JSON.stringify(jsonIndex), miniSearchOptions);
        console.log('✓ Loaded pre-compiled MiniSearch index from search-index.json');

        // Extract list of all package paths from loaded index
        const searchResults = miniSearchInstance.search('', { prefix: true });
        if (searchResults.length > 0) {
          allPackages = searchResults.map(r => r.path);
        }
        renderSidebarModules('');
        return;
      }
    } catch (err) {
      console.warn('Pre-compiled search-index.json not found, attempting dynamic fallback...', err);
    }

    // 2. Dynamic Fallback: fetch packages.json if available
    try {
      const res = await fetch('packages.json');
      if (res.ok) {
        const pkgList = await res.json();
        if (Array.isArray(pkgList) && pkgList.length > 0) {
          allPackages = pkgList;
          buildMiniSearchFromPackages(pkgList);
          renderSidebarModules('');
          return;
        }
      }
    } catch (err) {
      console.warn('packages.json fallback unavailable, indexing featured modules.', err);
    }

    // 3. Fallback: Index featured prototype modules
    buildMiniSearchFromPackages(allPackages);
    renderSidebarModules('');
  }

  /**
   * Build MiniSearch index dynamically from array of package paths
   */
  function buildMiniSearchFromPackages(pkgPaths) {
    miniSearchInstance = new MiniSearch(miniSearchOptions);
    const docs = pkgPaths.map((path, id) => {
      const parts = path.split('/');
      return {
        id: id + 1,
        path: path,
        name: parts[parts.length - 1],
        org: parts.length > 1 ? parts[1] : ''
      };
    });
    miniSearchInstance.addAll(docs);
  }

  /**
   * Perform instant search via MiniSearch engine
   */
  function performSearch(query) {
    const q = query.trim();
    if (!q) {
      return allPackages.slice(0, 30);
    }

    if (!miniSearchInstance) {
      // Fallback simple filter if index not ready
      return allPackages.filter(p => p.toLowerCase().includes(q.toLowerCase())).slice(0, 30);
    }

    const results = miniSearchInstance.search(q, {
      prefix: true,
      fuzzy: 0.2,
      boost: { name: 2, path: 1 }
    });

    return results.slice(0, 30).map(r => r.path);
  }

  /**
   * Update active state of top navbar links
   */
  function updateNavLinks(activeRoute) {
    if (DOM.navReportsLink) DOM.navReportsLink.classList.remove('active');
    if (DOM.navAboutLink) DOM.navAboutLink.classList.remove('active');
    if (DOM.navContactLink) DOM.navContactLink.classList.remove('active');

    if (activeRoute === 'about' && DOM.navAboutLink) {
      DOM.navAboutLink.classList.add('active');
    } else if (activeRoute === 'contact' && DOM.navContactLink) {
      DOM.navContactLink.classList.add('active');
    } else if (DOM.navReportsLink) {
      DOM.navReportsLink.classList.add('active');
    }
  }

  /**
   * Helper to set active view state (loading, error, report, about, contact)
   */
  function setViewState(state, extraMessage = '') {
    DOM.loadingState.classList.add('hidden');
    DOM.errorState.classList.add('hidden');
    DOM.reportView.classList.add('hidden');
    if (DOM.aboutView) DOM.aboutView.classList.add('hidden');
    if (DOM.contactView) DOM.contactView.classList.add('hidden');

    if (state === 'loading') {
      DOM.loadingModuleName.textContent = extraMessage;
      DOM.loadingState.classList.remove('hidden');
      updateNavLinks('reports');
    } else if (state === 'error') {
      DOM.errorMessageText.textContent = extraMessage || 'Report JSON not found for this module.';
      DOM.errorState.classList.remove('hidden');
      updateNavLinks('reports');
    } else if (state === 'report') {
      DOM.reportView.classList.remove('hidden');
      updateNavLinks('reports');
    } else if (state === 'about') {
      if (DOM.aboutView) DOM.aboutView.classList.remove('hidden');
      updateNavLinks('about');
    } else if (state === 'contact') {
      if (DOM.contactView) DOM.contactView.classList.remove('hidden');
      updateNavLinks('contact');
    }
  }

  /**
   * Render Sidebar Module Navigation safely using Vanilla JS
   */
  function renderSidebarModules(filterQuery = '') {
    DOM.moduleListContainer.replaceChildren();

    const currentHash = getModuleFromHash() || DEFAULT_MODULE;
    const matchingPackages = performSearch(filterQuery);

    matchingPackages.forEach(pkgName => {
      const a = document.createElement('a');
      a.href = '#' + pkgName;
      a.className = 'module-item' + (pkgName === currentHash ? ' active' : '');

      const nameSpan = document.createElement('span');
      nameSpan.className = 'module-item-name';
      nameSpan.textContent = pkgName;

      const gradeSpan = document.createElement('span');
      gradeSpan.className = `module-item-grade grade-${getGradeClass('C')}`;
      gradeSpan.textContent = 'SCORE';

      a.appendChild(nameSpan);
      a.appendChild(gradeSpan);

      DOM.moduleListContainer.appendChild(a);
    });

    DOM.moduleCountBadge.textContent = `${matchingPackages.length} Results`;
  }

  /**
   * Extract & sanitize route or module path from URL hash or pathname
   */
  function getRouteFromLocation() {
    // 1. Check path routing (/about, /contact)
    const pathname = window.location.pathname.replace(/\/+$/, '');
    if (pathname === '/about') return 'about';
    if (pathname === '/contact') return 'contact';

    // 2. Check hash routing (#about, #contact, #/about, #/contact)
    const rawHash = window.location.hash.substring(1).trim();
    if (!rawHash) return '';
    const cleanHash = rawHash.replace(/^\/+/, '');
    const sanitized = cleanHash.replace(/\.\.\//g, '').replace(/[\/\\]\.\.[\/\\]/g, '');

    if (sanitized === 'about' || sanitized === 'contact') {
      return sanitized;
    }
    return sanitized;
  }

  function getModuleFromHash() {
    const route = getRouteFromLocation();
    if (route === 'about' || route === 'contact') return '';
    return route;
  }

  /**
   * Helper to map Grade string to CSS class suffix
   */
  function getGradeClass(grade) {
    if (!grade) return 'c';
    const g = grade.toUpperCase().trim();
    if (g === 'A+') return 'aplus';
    if (g === 'A') return 'a';
    if (g === 'B') return 'b';
    if (g === 'C') return 'c';
    if (g === 'D') return 'd';
    if (g === 'F') return 'f';
    return 'c';
  }

  /**
   * Handle application routing based on URL location
   */
  function handleRoute() {
    const route = getRouteFromLocation();

    if (route === 'about') {
      setViewState('about');
      window.scrollTo(0, 0);
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', { page_path: '/about', page_title: 'About | goscore.dev' });
      }
      return;
    }

    if (route === 'contact') {
      setViewState('contact');
      window.scrollTo(0, 0);
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', { page_path: '/contact', page_title: 'Contact | goscore.dev' });
      }
      return;
    }

    const modulePath = route || DEFAULT_MODULE;
    loadReport(modulePath);
  }

  /**
   * Load report via AJAX fetch
   */
  async function loadReport(modulePath) {
    if (!modulePath) {
      modulePath = DEFAULT_MODULE;
    }

    setViewState('loading', modulePath);
    DOM.searchInput.value = modulePath;
    renderSidebarModules(DOM.sidebarFilterInput.value);

    // Build URL endpoint for static json file: reports/<modulePath>/report.json
    const reportUrl = `reports/${modulePath}/report.json`;

    try {
      const response = await fetch(reportUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Report not found for module '${modulePath}'`);
      }
      const data = await response.json();
      displayReport(data);

      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_path: `/#${modulePath}`,
          module: modulePath
        });
      }
    } catch (err) {
      console.warn('Failed to load report:', err);
      setViewState('error', `Could not find an analysis report for "${modulePath}". Make sure the module path is correct.`);
    }
  }

  /**
   * Safely render the Report JSON object into the DOM
   */
  function displayReport(report) {
    DOM.reportModuleTitle.textContent = report.module || 'Unknown Module';
    DOM.reportVersionTag.textContent = report.version || 'v0.0.0';
    
    if (report.timestamp) {
      const date = new Date(report.timestamp);
      DOM.reportTimestampText.textContent = `Analyzed ${date.toLocaleDateString()} at ${date.toLocaleTimeString()}`;
    } else {
      DOM.reportTimestampText.textContent = 'Timestamp unavailable';
    }

    const grade = (report.grade || 'C').toUpperCase();
    const score = typeof report.score === 'number' ? Math.round(report.score * 10) / 10 : 0;

    DOM.gradeBadge.textContent = grade;
    DOM.gradeBadge.className = `grade-badge grade-${getGradeClass(grade)}`;
    DOM.numericalScore.textContent = `${score}/100`;

    DOM.gaugeBarFill.style.width = `${Math.min(100, Math.max(0, score))}%`;

    renderCheckCards(report.checks || {});
    setViewState('report');
  }

  /**
   * Render individual static analysis check cards safely using Vanilla DOM elements
   */
  function renderCheckCards(checks) {
    DOM.checksGridContainer.replaceChildren();

    const checkDefinitions = [
      { key: 'govet', name: 'govet', label: 'Code Correctness (go vet)' },
      { key: 'govulncheck', name: 'govulncheck', label: 'Security Vulnerabilities' },
      { key: 'gofmt', name: 'gofmt', label: 'Formatting Compliance' },
      { key: 'gocyclo', name: 'gocyclo', label: 'Cyclomatic Complexity' },
      { key: 'go_mod_verify', name: 'go_mod_verify', label: 'Module Integrity' }
    ];

    checkDefinitions.forEach(def => {
      const checkData = checks[def.key];
      const card = createCheckCard(def, checkData);
      DOM.checksGridContainer.appendChild(card);
    });
  }

  /**
   * Safe DOM node factory for a single check card
   */
  function createCheckCard(def, data) {
    const card = document.createElement('div');
    card.className = 'check-card';

    const cardHeader = document.createElement('div');
    cardHeader.className = 'check-card-header';

    const nameBox = document.createElement('div');
    nameBox.className = 'check-name-box';

    const title = document.createElement('h3');
    title.className = 'check-title';
    title.textContent = def.name;

    nameBox.appendChild(title);

    const statusBadge = document.createElement('span');
    statusBadge.className = 'check-status-badge';

    if (!data) {
      statusBadge.classList.add('status-info');
      statusBadge.textContent = 'NO DATA';
    } else if (data.passed === true) {
      statusBadge.classList.add('status-pass');
      statusBadge.textContent = 'PASSED';
    } else if (data.passed === false) {
      statusBadge.classList.add('status-fail');
      statusBadge.textContent = 'FAILED';
    } else {
      statusBadge.classList.add('status-info');
      statusBadge.textContent = 'INFO';
    }

    cardHeader.appendChild(nameBox);
    cardHeader.appendChild(statusBadge);
    card.appendChild(cardHeader);

    const metricRow = document.createElement('div');
    metricRow.className = 'check-metric-row';

    const metricLabel = document.createElement('span');
    metricLabel.className = 'metric-label';
    metricLabel.textContent = def.label;

    const metricVal = document.createElement('span');
    metricVal.className = 'metric-value';
    metricVal.textContent = data && typeof data.score === 'number' ? `${data.score}/100` : 'N/A';

    metricRow.appendChild(metricLabel);
    metricRow.appendChild(metricVal);
    card.appendChild(metricRow);

    if (data) {
      if (def.key === 'govulncheck' && typeof data.vulnerabilities_found === 'number') {
        const vulnBox = document.createElement('div');
        vulnBox.className = 'detail-box';
        
        if (data.vulnerabilities_found === 0) {
          vulnBox.textContent = '✓ No known vulnerabilities detected.';
        } else {
          vulnBox.classList.add('detail-list-item');
          vulnBox.textContent = `⚠️ ${data.vulnerabilities_found} vulnerability/vulnerabilities identified by govulncheck.`;
        }
        card.appendChild(vulnBox);
      }

      if (def.key === 'gofmt' && Array.isArray(data.unformatted_files)) {
        if (data.unformatted_files.length > 0) {
          const detailBox = document.createElement('div');
          detailBox.className = 'detail-box';

          const listHeader = document.createElement('div');
          listHeader.style.fontWeight = '600';
          listHeader.style.marginBottom = '4px';
          listHeader.textContent = 'Unformatted Files:';
          detailBox.appendChild(listHeader);

          const ul = document.createElement('ul');
          ul.className = 'detail-list';

          data.unformatted_files.forEach(file => {
            const li = document.createElement('li');
            li.className = 'detail-list-item';
            li.textContent = file;
            ul.appendChild(li);
          });

          detailBox.appendChild(ul);
          card.appendChild(detailBox);
        } else {
          const detailBox = document.createElement('div');
          detailBox.className = 'detail-box';
          detailBox.textContent = '✓ All Go source files are cleanly formatted with gofmt.';
          card.appendChild(detailBox);
        }
      }

      if (def.key === 'gocyclo' && typeof data.average_complexity === 'number') {
        const cycloBox = document.createElement('div');
        cycloBox.className = 'detail-box';
        cycloBox.textContent = `Average Cyclomatic Complexity: ${data.average_complexity}`;
        card.appendChild(cycloBox);
      }

      if (def.key === 'govet' && data.output) {
        const govetBox = document.createElement('div');
        govetBox.className = 'detail-box';
        govetBox.textContent = data.output;
        card.appendChild(govetBox);
      }
    }

    return card;
  }

  // Autocomplete State
  let autocompleteSelectedIndex = -1;
  let autocompleteSuggestions = [];

  /**
   * Hide typeahead autocomplete dropdown
   */
  function hideAutocomplete() {
    if (DOM.autocompleteDropdown) {
      DOM.autocompleteDropdown.classList.add('hidden');
    }
    if (DOM.searchInput) {
      DOM.searchInput.setAttribute('aria-expanded', 'false');
    }
    autocompleteSelectedIndex = -1;
    autocompleteSuggestions = [];
  }

  /**
   * Render and display typeahead autocomplete suggestions
   */
  function showAutocomplete(query) {
    if (!DOM.autocompleteDropdown || !DOM.autocompleteItems) return;

    const q = query.trim();
    if (!q) {
      hideAutocomplete();
      return;
    }

    const results = performSearch(q);
    const topResults = results.slice(0, 8);
    autocompleteSuggestions = topResults;
    autocompleteSelectedIndex = -1;

    DOM.autocompleteItems.replaceChildren();

    if (topResults.length === 0) {
      const emptyDiv = document.createElement('div');
      emptyDiv.className = 'autocomplete-item';
      emptyDiv.style.cursor = 'default';
      emptyDiv.style.color = 'var(--text-dim)';
      emptyDiv.textContent = 'No matching Go modules found';
      DOM.autocompleteItems.appendChild(emptyDiv);
    } else {
      topResults.forEach((pkgPath, index) => {
        const item = document.createElement('div');
        item.className = 'autocomplete-item';
        item.setAttribute('role', 'option');
        item.setAttribute('id', `autocomplete-opt-${index}`);

        const nameSpan = document.createElement('span');
        nameSpan.className = 'autocomplete-item-name';

        // Add package svg icon
        const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        iconSvg.setAttribute('class', 'autocomplete-item-icon');
        iconSvg.setAttribute('width', '14');
        iconSvg.setAttribute('height', '14');
        iconSvg.setAttribute('viewBox', '0 0 24 24');
        iconSvg.setAttribute('fill', 'none');
        iconSvg.setAttribute('stroke', 'currentColor');
        iconSvg.setAttribute('stroke-width', '2');
        const iconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        iconPath.setAttribute('d', 'M16.5 9.4 7.55 4.24a1.78 1.78 0 0 0-2.5 1.55v12.42a1.78 1.78 0 0 0 2.5 1.55L16.5 14.6a1.78 1.78 0 0 0 0-3.2z');
        iconSvg.appendChild(iconPath);
        nameSpan.appendChild(iconSvg);

        // Safe highlighted text matching (no innerHTML)
        const lowerPath = pkgPath.toLowerCase();
        const lowerQ = q.toLowerCase();
        const matchIdx = lowerPath.indexOf(lowerQ);

        if (matchIdx !== -1) {
          const before = pkgPath.substring(0, matchIdx);
          const matched = pkgPath.substring(matchIdx, matchIdx + q.length);
          const after = pkgPath.substring(matchIdx + q.length);

          if (before) nameSpan.appendChild(document.createTextNode(before));

          const hlSpan = document.createElement('span');
          hlSpan.style.color = '#38bdf8';
          hlSpan.style.fontWeight = '600';
          hlSpan.textContent = matched;
          nameSpan.appendChild(hlSpan);

          if (after) nameSpan.appendChild(document.createTextNode(after));
        } else {
          nameSpan.appendChild(document.createTextNode(pkgPath));
        }

        const badge = document.createElement('span');
        badge.className = 'autocomplete-item-badge';
        badge.textContent = 'MODULE';

        item.appendChild(nameSpan);
        item.appendChild(badge);

        item.addEventListener('mouseenter', () => {
          setAutocompleteActiveIndex(index);
        });

        item.addEventListener('click', () => {
          DOM.searchInput.value = pkgPath;
          window.location.hash = pkgPath;
          hideAutocomplete();
        });

        DOM.autocompleteItems.appendChild(item);
      });
    }

    // Add keyboard hint footer
    const footer = document.createElement('div');
    footer.className = 'autocomplete-footer';

    const hintSpan = document.createElement('span');
    hintSpan.textContent = 'Navigation: ';
    const kbdUp = document.createElement('kbd');
    kbdUp.className = 'autocomplete-kbd';
    kbdUp.textContent = '↑';
    const kbdDown = document.createElement('kbd');
    kbdDown.className = 'autocomplete-kbd';
    kbdDown.textContent = '↓';
    const kbdEnter = document.createElement('kbd');
    kbdEnter.className = 'autocomplete-kbd';
    kbdEnter.textContent = 'Enter';
    const kbdEsc = document.createElement('kbd');
    kbdEsc.className = 'autocomplete-kbd';
    kbdEsc.textContent = 'Esc';

    hintSpan.appendChild(kbdUp);
    hintSpan.appendChild(document.createTextNode(' '));
    hintSpan.appendChild(kbdDown);
    hintSpan.appendChild(document.createTextNode(' navigate  '));
    hintSpan.appendChild(kbdEnter);
    hintSpan.appendChild(document.createTextNode(' select  '));
    hintSpan.appendChild(kbdEsc);
    hintSpan.appendChild(document.createTextNode(' close'));

    const countSpan = document.createElement('span');
    countSpan.textContent = `${results.length} matches`;

    footer.appendChild(hintSpan);
    footer.appendChild(countSpan);
    DOM.autocompleteItems.appendChild(footer);

    DOM.autocompleteDropdown.classList.remove('hidden');
    DOM.searchInput.setAttribute('aria-expanded', 'true');
  }

  /**
   * Set active index in autocomplete dropdown
   */
  function setAutocompleteActiveIndex(index) {
    autocompleteSelectedIndex = index;
    const items = DOM.autocompleteItems.querySelectorAll('.autocomplete-item[role="option"]');
    items.forEach((it, idx) => {
      if (idx === index) {
        it.classList.add('selected');
        it.scrollIntoView({ block: 'nearest' });
      } else {
        it.classList.remove('selected');
      }
    });
  }

  /**
   * Event Listeners Setup
   */
  function initEvents() {
    window.addEventListener('hashchange', () => {
      handleRoute();
    });

    window.addEventListener('popstate', () => {
      handleRoute();
    });

    DOM.searchForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const query = DOM.searchInput.value.trim();
      if (query) {
        window.location.hash = query;
      }
      hideAutocomplete();
    });

    // Real-time MiniSearch input filter & typeahead
    DOM.sidebarFilterInput.addEventListener('input', (e) => {
      renderSidebarModules(e.target.value);
    });

    DOM.searchInput.addEventListener('input', (e) => {
      const val = e.target.value;
      renderSidebarModules(val);
      showAutocomplete(val);
    });

    DOM.searchInput.addEventListener('focus', (e) => {
      if (e.target.value.trim()) {
        showAutocomplete(e.target.value);
      }
    });

    DOM.searchInput.addEventListener('keydown', (e) => {
      const isHidden = !DOM.autocompleteDropdown || DOM.autocompleteDropdown.classList.contains('hidden');

      if (isHidden) {
        if (e.key === 'ArrowDown' && DOM.searchInput.value.trim()) {
          showAutocomplete(DOM.searchInput.value);
          e.preventDefault();
        }
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (autocompleteSuggestions.length > 0) {
          const nextIndex = (autocompleteSelectedIndex + 1) % autocompleteSuggestions.length;
          setAutocompleteActiveIndex(nextIndex);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (autocompleteSuggestions.length > 0) {
          const prevIndex = autocompleteSelectedIndex <= 0 ? autocompleteSuggestions.length - 1 : autocompleteSelectedIndex - 1;
          setAutocompleteActiveIndex(prevIndex);
        }
      } else if (e.key === 'Enter') {
        if (autocompleteSelectedIndex >= 0 && autocompleteSelectedIndex < autocompleteSuggestions.length) {
          e.preventDefault();
          const selectedPkg = autocompleteSuggestions[autocompleteSelectedIndex];
          DOM.searchInput.value = selectedPkg;
          window.location.hash = selectedPkg;
          hideAutocomplete();
        }
      } else if (e.key === 'Escape') {
        hideAutocomplete();
      } else if (e.key === 'Tab') {
        if (autocompleteSelectedIndex >= 0 && autocompleteSelectedIndex < autocompleteSuggestions.length) {
          DOM.searchInput.value = autocompleteSuggestions[autocompleteSelectedIndex];
        }
      }
    });

    // Close autocomplete on click outside
    document.addEventListener('click', (e) => {
      if (DOM.searchContainer && !DOM.searchContainer.contains(e.target)) {
        hideAutocomplete();
      }
    });

    DOM.copyUrlBtn.addEventListener('click', () => {
      const currentUrl = window.location.href;
      navigator.clipboard.writeText(currentUrl).then(() => {
        DOM.copyBtnText.textContent = 'Copied!';
        setTimeout(() => {
          DOM.copyBtnText.textContent = 'Share Report';
        }, 2000);
      }).catch(() => {
        DOM.copyBtnText.textContent = 'Copied!';
      });
    });

    DOM.errorRetryBtn.addEventListener('click', () => {
      window.location.hash = DEFAULT_MODULE;
    });
  }

  /**
   * Application Initialization
   */
  async function init() {
    initEvents();
    await initMiniSearch();

    handleRoute();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
